import React, { useState, useCallback } from 'react';
import { Table, Button, Tag, message, Modal, Select, Space, Dropdown, Menu } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import moment from 'moment';

import Layout from '../../../components/Layout/Layout';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import PageSubtitle from '../../../components/PageSubtitle/PageSubtitle';

import useLocale from '../../../hooks/useLocale';
import API from '../../../utils/api';
import { USE_QUERY_OPTIONS } from '../../../constants/reactQuery';
import { ClientService } from '../../../shared/api/ClientService';

import './PendingAuth0Changes.scss';

const { Option } = Select;
const { confirm } = Modal;

const PENDING_AUTH0_CHANGES_QUERY = 'PENDING_AUTH0_CHANGES';

function PendingAuth0Changes(): JSX.Element {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<number | null>(0); // 0 = Pending
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [runTour, setRunTour] = useState(false);

  const tourSteps: Step[] = [
    {
      target: 'body',
      content: (
        <div>
          <h3>{t.TOUR_STEP_WELCOME_TITLE}</h3>
          <p>{t.TOUR_STEP_WELCOME_CONTENT}</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="status-filter"]',
      title: t.TOUR_STEP_FILTER_TITLE,
      content: t.TOUR_STEP_FILTER_CONTENT,
      disableBeacon: true,
    },
    {
      target: '[data-tour="pending-changes-table"]',
      title: t.TOUR_STEP_TABLE_TITLE,
      content: t.TOUR_STEP_TABLE_CONTENT,
      disableBeacon: true,
    },
    {
      target: '[data-tour="col-requested-changes"]',
      title: t.TOUR_STEP_REQUESTED_CHANGES_TITLE,
      content: t.TOUR_STEP_REQUESTED_CHANGES_CONTENT,
      disableBeacon: true,
    },
    {
      target: '[data-tour="col-reason"]',
      title: t.TOUR_STEP_REASON_TITLE,
      content: t.TOUR_STEP_REASON_CONTENT,
      disableBeacon: true,
    },
    {
      target: '[data-tour="col-status"]',
      title: t.TOUR_STEP_STATUS_TITLE,
      content: t.TOUR_STEP_STATUS_CONTENT,
      disableBeacon: true,
    },
    {
      target: '[data-tour="col-actions"]',
      title: t.TOUR_STEP_ACTIONS_TITLE,
      content: t.TOUR_STEP_ACTIONS_CONTENT,
      disableBeacon: true,
    },
  ];

  const handleTourCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
    }
  };

  const { data, isLoading } = useQuery<ClientService.PendingAuth0ChangeListDto>(
    [PENDING_AUTH0_CHANGES_QUERY, statusFilter, pageSize, currentPage],
    () =>
      API.pendingAuth0Changes(statusFilter === null ? undefined : statusFilter, pageSize, (currentPage - 1) * pageSize),
    {
      ...USE_QUERY_OPTIONS,
      enabled: true,
    }
  );

  const approveMutation = useMutation((id: string) => API.approve(id), {
    onSuccess: () => {
      message.success(t.AUTH0_CHANGE_APPROVED_SUCCESSFULLY);
      queryClient.invalidateQueries([PENDING_AUTH0_CHANGES_QUERY]);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error?.message || t.FAILED_TO_APPROVE_AUTH0_CHANGE);
    },
  });

  const rejectMutation = useMutation((id: string) => API.reject(id), {
    onSuccess: () => {
      message.success(t.AUTH0_CHANGE_REJECTED_SUCCESSFULLY);
      queryClient.invalidateQueries([PENDING_AUTH0_CHANGES_QUERY]);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error?.message || t.FAILED_TO_REJECT_AUTH0_CHANGE);
    },
  });

  const handleApprove = useCallback(
    (record: ClientService.PendingAuth0ChangeDto) => {
      if (!record.id) return;
      confirm({
        title: t.APPROVE_AUTH0_CHANGE,
        icon: <ExclamationCircleOutlined />,
        content: t.APPROVE_AUTH0_CHANGE_CONFIRMATION,
        okText: t.APPROVE,
        cancelText: t.CANCEL,
        onOk: () => approveMutation.mutate(record.id!),
      });
    },
    [approveMutation, t]
  );

  const handleReject = useCallback(
    (record: ClientService.PendingAuth0ChangeDto) => {
      if (!record.id) return;
      confirm({
        title: t.REJECT_AUTH0_CHANGE,
        icon: <ExclamationCircleOutlined />,
        content: t.REJECT_AUTH0_CHANGE_CONFIRMATION,
        okText: t.REJECT,
        okType: 'danger',
        cancelText: t.CANCEL,
        onOk: () => rejectMutation.mutate(record.id!),
      });
    },
    [rejectMutation, t]
  );

  const getStatusTag = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <Tag color="orange">{t.PENDING}</Tag>;
      case 'approved':
        return <Tag color="green">{t.APPROVED}</Tag>;
      case 'rejected':
        return <Tag color="red">{t.REJECTED}</Tag>;
      case 'failed':
        return <Tag color="volcano">{t.FAILED}</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const parseRequestedData = (jsonString: string, changeTypeDisplay?: string) => {
    try {
      const data = JSON.parse(jsonString);
      return (
        <div>
          {changeTypeDisplay && (
            <div>
              <strong>{changeTypeDisplay}</strong>
            </div>
          )}
          {data.emailAddress && (
            <div>
              {t.EMAIL}: {data.emailAddress}
            </div>
          )}
          {data.language && (
            <div>
              {t.LANGUAGE}: {data.language}
            </div>
          )}
        </div>
      );
    } catch {
      return jsonString;
    }
  };

  const columns: ColumnsType<ClientService.PendingAuth0ChangeDto> = [
    {
      title: t.DASHBOARD_TABLE_FILE_NAME,
      dataIndex: 'fileName',
      key: 'fileName',
      width: 120,
    },
    {
      title: t.CLIENT_NAME,
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150,
    },
    {
      title: t.REQUESTED_CHANGES,
      dataIndex: 'requestedData',
      key: 'requestedData',
      width: 220,
      onHeaderCell: () => ({ 'data-tour': 'col-requested-changes' } as React.HTMLAttributes<HTMLElement>),
      render: (text: string, record: ClientService.PendingAuth0ChangeDto) =>
        parseRequestedData(text, record.changeTypeDisplay),
    },
    {
      title: t.REASON,
      dataIndex: 'reason',
      key: 'reason',
      width: 250,
      ellipsis: true,
      onHeaderCell: () => ({ 'data-tour': 'col-reason' } as React.HTMLAttributes<HTMLElement>),
    },
    {
      title: t.STATUS,
      dataIndex: 'statusDisplay',
      key: 'statusDisplay',
      width: 100,
      onHeaderCell: () => ({ 'data-tour': 'col-status' } as React.HTMLAttributes<HTMLElement>),
      render: (text: string) => getStatusTag(text),
    },
    {
      title: t.CREATED,
      dataIndex: 'creationTime',
      key: 'creationTime',
      width: 150,
      render: (text: string) => moment(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t.REVIEWED_BY,
      dataIndex: 'reviewedByName',
      key: 'reviewedByName',
      width: 120,
    },
    {
      title: t.REVIEWED_AT,
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      width: 150,
      render: (text: string) => (text ? moment(text).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: t.ACTIONS,
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      onHeaderCell: () => ({ 'data-tour': 'col-actions' } as React.HTMLAttributes<HTMLElement>),
      render: (_: any, record: ClientService.PendingAuth0ChangeDto) => {
        if (record.statusDisplay?.toLowerCase() === 'pending') {
          const menu = (
            <Menu>
              <Menu.Item key="approve" icon={<CheckOutlined />} onClick={() => handleApprove(record)}>
                {t.APPROVE}
              </Menu.Item>
              <Menu.Item key="reject" icon={<CloseOutlined />} danger onClick={() => handleReject(record)}>
                {t.REJECT}
              </Menu.Item>
            </Menu>
          );
          return (
            <Dropdown overlay={menu} trigger={['click']}>
              <Button size="small">
                {t.ACTIONS} <DownOutlined />
              </Button>
            </Dropdown>
          );
        }
        return '-';
      },
    },
  ];

  return (
    <Layout>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        callback={handleTourCallback}
        locale={{
          back: t.TOUR_BACK,
          close: t.TOUR_CLOSE,
          last: t.TOUR_LAST,
          next: t.TOUR_NEXT,
          skip: t.TOUR_SKIP,
        }}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#1890ff',
          },
        }}
      />
      <Breadcrumb
        data={[
          { title: t.HOME, link: '/' },
          { title: t.ADMINISTRATION, link: '/administration' },
          { title: t.PENDING_AUTH0_CHANGES },
        ]}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageSubtitle title={t.PENDING_AUTH0_CHANGES} />
        <Button icon={<QuestionCircleOutlined />} onClick={() => setRunTour(true)} style={{ marginBottom: 16 }}>
          {t.TOUR_START_WALKTHROUGH}
        </Button>
      </div>

      <div className="pending-auth0-changes">
        <div className="pending-auth0-changes__filters" data-tour="status-filter">
          <Space>
            <span>{t.STATUS}:</span>
            <Select
              style={{ width: 150 }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
            >
              <Option value={null}>{t.ALL}</Option>
              <Option value={0}>{t.PENDING}</Option>
              <Option value={1}>{t.APPROVED}</Option>
              <Option value={2}>{t.REJECTED}</Option>
              <Option value={3}>{t.FAILED}</Option>
            </Select>
          </Space>
        </div>

        <Table
          data-tour="pending-changes-table"
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 1330 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: data?.totalCount || 0,
            showSizeChanger: true,
            showTotal: (total) => `${t.TOTAL} ${total} ${t.ITEMS}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 20);
            },
          }}
        />
      </div>
    </Layout>
  );
}

export default PendingAuth0Changes;
